import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useFrontPageCta from "../../Hooks/useFrontPageCta";

// Assets
import { IckonicLogo } from "../../Assets/SVGs/Logos";

// Components
import ButtonPillGradient from "../Buttons/ButtonPillGradient";

// Constants
import { RoutePaths } from "../../Constants/RoutePaths";
import { ResponsiveBreakpoints } from "../../Constants/ResponsiveBreakpoints";

// Context
import { GlobalInterfaceContext } from "../../Context/GlobalInterfaceContext";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";

const desktopSectionLinks = [
    { label: "Community", href: "#community" },
    { label: "Watch trailer", href: "#trailer" },
    { label: "Presenters", href: "#presenters" },
    { label: "What to watch", href: "#originals" },
    { label: "Apps", href: "#apps" },
    { label: "Pricing", href: "#pricing" },
];

const mobileSectionLinks = desktopSectionLinks;

const FrontPageHeader: React.FC = () => {
    const cta = useFrontPageCta("Start watching", RoutePaths.SignUp[0]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuToggleRef = useRef<HTMLButtonElement>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const {
        toggleMainMenuDesktopMode,
        toggleMainMenuOpenState,
    } = useContext(GlobalInterfaceContext);
    const { doesAuthTokenExist } = useContext(UserAuthenticationContext);
    const isAuthenticated = doesAuthTokenExist?.() ?? false;

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const handleSectionLinkClick = (
        event: React.MouseEvent<HTMLAnchorElement>,
        href: string
    ) => {
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        closeMenu();

        const target = document.getElementById(href.replace(/^#/, ""));
        if (!target || typeof target.scrollIntoView !== "function") return;

        event.preventDefault();
        const prefersReducedMotion =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        target.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
        });
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: href,
            },
            { preventScrollReset: true }
        );
        target.focus({ preventScroll: true });
    };

    useEffect(() => {
        if (!isMenuOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsMenuOpen(false);
                menuToggleRef.current?.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isMenuOpen]);

    useEffect(() => {
        if (typeof window.matchMedia !== "function") return;

        const desktopMode = window.matchMedia(
            `(min-width: ${ResponsiveBreakpoints.DesktopBreakpoint}px)`
        );
        const updateDesktopMode = (matches: boolean) => {
            toggleMainMenuDesktopMode?.(matches);
            toggleMainMenuOpenState?.(false);

            if (matches) {
                setIsMenuOpen(false);
            }
        };
        const handleDesktopModeChange = (event: MediaQueryListEvent) => {
            updateDesktopMode(event.matches);
        };

        updateDesktopMode(desktopMode.matches);
        desktopMode.addEventListener("change", handleDesktopModeChange);

        return () => {
            desktopMode.removeEventListener("change", handleDesktopModeChange);
        };
    }, [toggleMainMenuDesktopMode, toggleMainMenuOpenState]);

    return (
        <header className="front-page-header">
            <div className="front-page-header__inner">
                <a
                    className="front-page-header__brand"
                    href="#top"
                    aria-label="Ickonic home"
                    onClick={closeMenu}
                >
                    {IckonicLogo()}
                </a>

                <nav
                    className="front-page-header__desktop-nav"
                    aria-label="Main navigation"
                >
                    {desktopSectionLinks.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            onClick={(event) =>
                                handleSectionLinkClick(event, link.href)
                            }
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="front-page-header__actions">
                    <Link
                        className="front-page-header__text-action"
                        to={isAuthenticated ? RoutePaths.Browse : RoutePaths.Login}
                    >
                        {isAuthenticated ? "Browse" : "Sign in"}
                    </Link>

                    <ButtonPillGradient
                        text={cta.text}
                        link={cta.link}
                        className="front-page-header__primary-action"
                        background="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"
                        backgroundHover="#b760b4"
                        color="white"
                    />

                    <button
                        ref={menuToggleRef}
                        className={`front-page-header__menu-toggle ${isMenuOpen ? "is-open" : "is-closed"}`}
                        type="button"
                        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={isMenuOpen}
                        aria-controls="front-page-mobile-menu"
                        onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
                    >
                        <span />
                        <span />
                    </button>
                </div>
            </div>

            <nav
                id="front-page-mobile-menu"
                className="front-page-header__mobile-menu"
                aria-label="Mobile navigation"
                hidden={!isMenuOpen}
            >
                {mobileSectionLinks.map((link) => (
                    <a
                        key={link.href}
                        href={link.href}
                        onClick={(event) =>
                            handleSectionLinkClick(event, link.href)
                        }
                    >
                        {link.label}
                    </a>
                ))}
            </nav>
        </header>
    );
};

export default FrontPageHeader;
